import api from "../api/api";
import { logger } from "../utils/logger";
import { getWalkthroughResumePath } from "../utils/walkthroughResume";
// Define user types and routes
const ROUTES = {
  TERMS: "/terms",
  THERAPIST_REDIRECT_ROUTE: "/clients",
  CLIENT_REDIRECT_ROUTE: "/programs",
  RESET_PASSWORD_ROUTE: "/reset-password/",
  VALIDATE_RESET_PASSWORD_ROUTE: "/reset-password-validate/",
  CONFIRM_RESET_PASSWORD_ROUTE: "/reset-password-confirm/",
};

const AUTH_STORAGE_KEYS = [
  "access_token",
  "refresh_token",
  "is_therapist",
  "user_id",
  "user_email",
  "is_staff",
  "is_clinic_admin",
  "management_only",
  "first_name",
];

export const clearAuthData = () => {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

// Store auth data in localStorage
const storage = {
  setAuthData: (data) => {
    localStorage.setItem("access_token", data.access);
    localStorage.setItem("refresh_token", data.refresh);
    localStorage.setItem("is_therapist", data.is_therapist);
    localStorage.setItem("user_id", data.user_id);
    localStorage.setItem("user_email", data.user_email);
    localStorage.setItem("is_staff", data.is_staff);
    localStorage.setItem("is_clinic_admin", Boolean(data.is_clinic_admin));
    localStorage.setItem("management_only", Boolean(data.management_only));
    localStorage.setItem("first_name", data.first_name);
  },

  clearAuthData,

  getAuthData: () => ({
    accessToken: localStorage.getItem("access_token"),
    refreshToken: localStorage.getItem("refresh_token"),
    isTherapist: localStorage.getItem("is_therapist") === "true",
    userId: localStorage.getItem("user_id"),
    isStaff: localStorage.getItem("is_staff") === "true",
  }),
};

// Authentication service
const authService = {
  login: async (email, password) => {
    email = email.toLowerCase().trim();
    try {
      const response = await api.post("/login/", {
        email,
        password,
      });

      // Store auth data
      storage.setAuthData(response.data);

      // Handle redirection
      if (!response.data.agreed) {
        window.location.href = ROUTES.TERMS;
      } else if (response.data.is_clinic_admin && (!response.data.is_therapist || response.data.management_only)) {
        window.location.href = "/team";
      } else if (response.data.is_therapist) {
        let redirectRoute = ROUTES.THERAPIST_REDIRECT_ROUTE;

        try {
          const walkthroughResponse = await api.get("/therapist-walkthrough/");
          redirectRoute =
            getWalkthroughResumePath(walkthroughResponse.data) ||
            ROUTES.THERAPIST_REDIRECT_ROUTE;
        } catch (walkthroughError) {
          logger.error("Walkthrough resume lookup failed:", {
            url: "/therapist-walkthrough/",
            error: walkthroughError,
          });
        }

        window.location.href = redirectRoute;
      } else {
        window.location.href = ROUTES.CLIENT_REDIRECT_ROUTE;
      }

      return {
        success: true,
        message: "Login successful!",
      };
    } catch (error) {
      logger.error("Login error:", { url: "/login/", error });
      if (error.response?.data?.error) {
        return {
          success: false,
          message: error.response.data.error,
        };
      }
      return {
        success: false,
        message: "An unexpected login error occurred. Please try again.",
      };
    }
  },

  logout: async (inactive = false) => {
    await api.post("/logout/");
    storage.clearAuthData();
    window.location.href = inactive ? "/logged-out" : "/";
  },
  refresh: async () => {
    try {
      const response = await api.post("/token/refresh/", {
        refresh: localStorage.getItem("refresh_token"),
      });

      if (response.status === 200) {
        localStorage.setItem("access_token", response.data.access);
      }
    } catch (error) {
      logger.error("Refresh token error:", {url: "/token/refresh/", error});
      return {
        success: false,
        message: "An unexpected refresh error occurred. Please try again.",
      };
    }
  },
  validateResetLink: async (uid, token) => {
    try {
      const response = await api.post("/reset-password-validate/", {
        uid,
        token
      });

      return {
        success: true,
        validLink: response.data.valid,
        email: response.data.email,
        hasPassword: response.data.hasPassword
      };
    } catch (error) {
      return {
        success: false,
        validLink: false,
        message: "Invalid or expired reset link"
      };
    }
  },
  resetPassword: async (email) => {
    try {
      await api.post("/reset-password/", { email });

      return {
        success: true,
        message:
          "Password reset link sent to your email. Please check your inbox.",
      };
    } catch (error) {
      // Handle different error scenarios
      if (error.response && error.response.status === 404) {
        return {
          message: "No account found with this email address.",
          success: false,
        };
      } else {
        return { message: "An error occurred. Please try again later." };
      }
    }
  },
  confirmPasswordReset: async (uid, token, newPassword) => {
    try {
      const response = await api.post("/reset-password-confirm/", {
        uid,
        token,
        new_password: newPassword,
      });

      return {
        success: true,
        message: "Password reset successful. Redirecting to login...",
        email: response.data.email,
      };
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.message || "Password reset failed. Please try again.",
      };
    }
  },

  isAuthenticated: () => {
    const { accessToken, refreshToken } = storage.getAuthData();
    return !!(accessToken && refreshToken);
  },
  getUserType: () => {
    const { isStaff, isTherapist } = storage.getAuthData();

    if (isStaff) return "staff";
    if (isTherapist) return "therapist";
    if (authService.isClinicAdmin()) return "clinic-admin";
    return "client";
  },

  // Helper methods for type checking
  isStaff: () => {
    return storage.getAuthData().isStaff;
  },

  isClinicAdmin: () => localStorage.getItem("is_clinic_admin") === "true",
  isManagementOnly: () => localStorage.getItem("management_only") === "true",

  isTherapist: () => {
    return storage.getAuthData().isTherapist;
  },

  isClient: () => {
    const { isStaff, isTherapist } = storage.getAuthData();
    return !isStaff && !isTherapist && !authService.isClinicAdmin();
  },
};

export default authService;
